import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // =========================================
  // STEP 1
  // =========================================
  step1: {
    name: "",
    country_id: "",
    city_id: null,
    product_type_id: 6,
    package_type_id: null,
    address: "",
    latitude: null,
    longitude: null,
    phone: "",
    has_guide: false,
    max_adults: 1,
    min_adults: 1,
    min_child: 0,
    max_child: 0,
    min_senior: 0,
    max_senior: 0,
    min_baby: 0,
    max_baby: 0,
    pax_number_min: 1,
    pax_number_max: 1,

    guide_in_person: [],
    guide_audio: [],
    guide_information: [],
  },

  // =========================================
  // STEP 2
  // =========================================
  step2: {
    start_date: "",
    end_date: "",
    short_summary: "",
    detailed_description: "",
    key_points: [],
    included_items: [],
    excluded_items: [],
    not_suitable_for: [""],
    prohibited_items: [],
    required_items: [],
    additional_info: "",
    keyword: [],
  },

  // =========================================
  // STEP 3 — ITINERARY
  // =========================================
  step3: {
    itinerary_days: [
      {
        day_number: 1,
        destination: "",
        description: "",
        is_accommodation_included: false,
        is_transport_included: false,

        accommodation_reference: {
          accommodation_type: "",
          name: "",
          address: "",
          category: "",
          product_room_type_id: [],
          board_included: false,
          breakfast_included: false,
          half_board_included: false,
          full_board_included: false,
          evening_included: false,
        },

        transport_reference: {
          transport_type: "",
          company_name: "",
          departure_location: "",
          arrival_location: "",
          departure_time: "",
          arrival_time: "",
          baggage_included: false,
          travel_number: "",
          fare_class: "",
          duration_value: "",
          duration_unit: "",
        },
      },
    ],
  },

  // =========================================
  // STEP 4 — LOGISTICS + PRICING
  // =========================================
  step4: {
    meeting_point: "",
    has_dropoff_location: false,
    dropoff_location: "",
    cutoff_time_booking_value: 0,
    cutoff_time_booking_unit: "minutes",
    cancellation_policy_id: "",
    booking_confirmation_mode: "",

    arrival_time_value: 0,
    arrival_time_unit: "minutes",
    charge_service: "",
    charge_service_type: "",

    pricing: {
      adult_price: "",
      child_price: "",
      senior_price: "",
      baby_price: "",
    },
  },

  images: [],
};

// ============================================
// Deep Update Helper
// ============================================
function deepUpdate(obj, field, value) {
  if (obj && typeof obj === "object") {
    if (field in obj) {
      obj[field] = value;
      return true;
    }
    for (const key of Object.keys(obj)) {
      if (deepUpdate(obj[key], field, value)) return true;
    }
  }
  return false;
}

const packageSlice = createSlice({
  name: "package",
  initialState,
  reducers: {
    // =====================================
    // Generic field update
    // =====================================
    updatePackageField: (state, action) => {
      const { field, value } = action.payload;

      if (typeof field !== "string") {
        console.error("updatePackageField: invalid field name", field);
        return;
      }

      if (field.includes(".")) {
        const keys = field.split(".");
        let target = state;

        for (let i = 0; i < keys.length - 1; i++) {
          if (!target[keys[i]]) return;
          target = target[keys[i]];
        }

        target[keys[keys.length - 1]] = value;
        return;
      }

      if (!deepUpdate(state, field, value)) {
        console.warn(`Field '${field}' not found`);
      }
    },

    // =====================================
    // Bulk Step Update
    // =====================================
    updatePackageStepBulk: (state, action) => {
      const { step, data } = action.payload;
      if (state[step]) {
        state[step] = { ...state[step], ...data };
      }
    },

    // =====================================
    // Itinerary Methods
    // =====================================
    updateItineraryDay: (state, action) => {
      const { index, data } = action.payload;
      if (state.step3.itinerary_days[index]) {
        state.step3.itinerary_days[index] = {
          ...state.step3.itinerary_days[index],
          ...data,
        };
      }
    },

    updateItineraryField: (state, action) => {
      const { index, field, value } = action.payload;
      const day = state.step3.itinerary_days[index];
      if (!day) return;

      if (field.includes(".")) {
        const keys = field.split(".");
        let target = day;

        for (let i = 0; i < keys.length - 1; i++) {
          if (!target[keys[i]]) return;
          target = target[keys[i]];
        }

        target[keys[keys.length - 1]] = value;
      } else {
        day[field] = value;
      }
    },

    addItineraryDay: (state) => {
      state.step3.itinerary_days.push({
        day_number: state.step3.itinerary_days.length + 1,
        destination: "",
        description: "",
        is_accommodation_included: false,
        is_transport_included: false,
        accommodation_reference: {
          accommodation_type: "",
          name: "",
          address: "",
          category: "",
          product_room_type_id: [],
          board_included: false,
          breakfast_included: false,
          half_board_included: false,
          full_board_included: false,
          evening_included: false,
        },
        transport_reference: {
          transport_type: "",
          company_name: "",
          departure_location: "",
          arrival_location: "",
          departure_time: "",
          arrival_time: "",
          baggage_included: false,
          travel_number: "",
          fare_class: "",
          duration_value: "",
          duration_unit: "",
        },
      });
    },

    deleteItineraryDay: (state, action) => {
      const { index } = action.payload;
      if (index >= 0 && state.step3.itinerary_days[index]) {
        state.step3.itinerary_days.splice(index, 1);

        // Recalculate day_numbers for all remaining days
        state.step3.itinerary_days.forEach((day, newIndex) => {
          day.day_number = newIndex + 1;
        });
      }
    },

    resetPackage: () => initialState,

    updateAccommodationField: (state, action) => {
      const { index, key, value } = action.payload;

      // Ensure the itinerary day exists
      if (!state.step3.itinerary_days[index]) return;

      // Ensure accommodation_reference exists
      if (!state.step3.itinerary_days[index].accommodation_reference) {
        state.step3.itinerary_days[index].accommodation_reference = {};
      }

      // Update field
      state.step3.itinerary_days[index].accommodation_reference[key] = value;
    },

    updateTransportField: (state, action) => {
      const { index, key, value } = action.payload;

      // Ensure the itinerary day exists
      if (!state.step3.itinerary_days[index]) return;

      // Ensure transport_reference exists
      if (!state.step3.itinerary_days[index].transport_reference) {
        state.step3.itinerary_days[index].transport_reference = {};
      }

      // Update field
      state.step3.itinerary_days[index].transport_reference[key] = value;
    },

    resetAccommodationField: (state, action) => {
      const { index } = action.payload;

      // Ensure the itinerary day exists
      if (!state.step3.itinerary_days[index]) return;

      // Reset accommodation_reference
      state.step3.itinerary_days[index].accommodation_reference = {
        accommodation_type: "",
        name: "",
        address: "",
        category: "",
        product_room_type_id: [],
        board_included: false,
        breakfast_included: false,
        half_board_included: false,
        full_board_included: false,
        evening_included: false,
      };
    },

    resetTransportField: (state, action) => {
      const { index } = action.payload;

      // Ensure the itinerary day exists
      if (!state.step3.itinerary_days[index]) return;

      // Reset transport_reference
      state.step3.itinerary_days[index].transport_reference = {
        transport_type: "",
        company_name: "",
        departure_location: "",
        arrival_location: "",
        departure_time: "",
        arrival_time: "",
        baggage_included: false,
        travel_number: "",
        fare_class: "",
        duration_value: "",
        duration_unit: "",
      };
    },
  },
});

export const {
  updatePackageField,
  updatePackageStepBulk,
  updateItineraryDay,
  updateItineraryField,
  addItineraryDay,
  deleteItineraryDay,
  addPackageImage,
  removePackageImage,
  resetPackage,
  updateAccommodationField,
  updateTransportField,
  resetAccommodationField,
  resetTransportField,
} = packageSlice.actions;

export default packageSlice.reducer;
